import { routes } from "@/consts";
import { getData } from "@/lib/utils";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import React from "react";
import { Link } from "react-router-dom";
import {
  Mail,
  Phone,
  MapPin,
  ExternalLink,
  Facebook,
  Twitter,
  Linkedin,
  Instagram,
} from "lucide-react";

const Footer: React.FC = () => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const accesToken = getData("tokens")?.accessToken;

  const socialLinks = [
    { icon: Facebook, href: "#", label: t("footer.socialMedia.facebook") },
    { icon: Twitter, href: "#", label: t("footer.socialMedia.twitter") },
    { icon: Linkedin, href: "#", label: t("footer.socialMedia.linkedin") },
    { icon: Instagram, href: "#", label: t("footer.socialMedia.instagram") },
  ];

  return (
    <footer className="bg-white border-t border-gray-200">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Medex</h1>
            <p className="text-gray-600 mb-6 leading-relaxed">
              {t("footer.companyDescription")}
            </p>

            {/* Social Links */}
            <div className="flex space-x-3">
              {socialLinks.map((social, index) => {
                const IconComponent = social.icon;
                return (
                  <a
                    key={index}
                    href={social.href}
                    className="w-8 h-8 bg-gray-100 hover:bg-blue-100 rounded-full flex items-center justify-center transition-colors duration-200"
                    aria-label={social.label}
                  >
                    <IconComponent className="w-4 h-4 text-gray-600 hover:text-blue-600 transition-colors duration-200" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {t("footer.quickLinks")}
            </h3>
            <ul className="space-y-2">
              {!accesToken && (
                <li>
                  <Link
                    to={routes.register}
                    className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors duration-200"
                  >
                    <ExternalLink className="w-4 h-4" />
                    {t("actions.registerAsSeller")}
                  </Link>
                </li>
              )}
              <li>
                <Link
                  to={routes.aboutUs}
                  className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors duration-200"
                >
                  <ExternalLink className="w-4 h-4" />
                  {t("general.aboutUs")}
                </Link>
              </li>
              <li>
                <Link
                  to={routes.home}
                  className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors duration-200"
                >
                  <ExternalLink className="w-4 h-4" />
                  {t("navigation.Help")}
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {t("footer.services")}
            </h3>
            <ul className="space-y-2">
              <li>
                <Link
                  to={routes.marketPlace}
                  className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors duration-200"
                >
                  <ExternalLink className="w-4 h-4" />
                  {t("navigation.Marketplace")}
                </Link>
              </li>
              <li>
                <Link
                  to={routes.tenders}
                  className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors duration-200"
                >
                  <ExternalLink className="w-4 h-4" />
                  {t("navigation.Tenders")}
                </Link>
              </li>
              <li>
                <Link
                  to={routes.payLater}
                  className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors duration-200"
                >
                  <ExternalLink className="w-4 h-4" />
                  {t("payLater.title")}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {t("footer.contactInfo.title")}
            </h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <Mail className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">
                    {t("footer.contactInfo.email")}
                  </p>
                  <a
                    href="mailto:support@medex2b.com"
                    className="text-gray-600 hover:text-blue-600 transition-colors duration-200"
                  >
                    support@medex2b.com
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <Phone className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">
                    {t("footer.contactInfo.phone")}
                  </p>
                  <a
                    href="tel:+966501234567"
                    className="text-gray-600 hover:text-blue-600 transition-colors duration-200"
                  >
                    +966 53 097 5200
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                  <MapPin className="w-4 h-4 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">
                    {t("footer.contactInfo.address")}
                  </p>
                  <p className="text-gray-600">
                    {t("footer.contactInfo.location")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
